import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, Key, SorterResult } from 'antd/lib/table/interface';
import isEmpty from 'lodash/isEmpty';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { WARNING_COLOR } from '../../../theme';
import Tooltip from 'antd/lib/tooltip';
import React, { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompanies } from 'src/components/selectors/GlobalCompanySelector/CompanyContext';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { UtilityOrIsoTag } from 'src/components/UtilityOrIsoTag';
import { compareWithLocale } from 'src/domain/compareWithLocale';
import { AbilityContext } from '../../../components/ability/can';
import { NominationStatusTag } from '../../../components/nominations/readiness-tracking/NominationStatusTag';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { ScheduleNominationReadinessModal } from '../../../components/program/ScheduleNominationReadinessModal';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { filterCompaniesByType } from '../../../components/selectors/GlobalCompanySelector/companyHelpers';
import { SiteAddress } from '../../../components/site/SiteAddress';
import { SiteMarketAndLoadZone } from '../../../components/site/SiteMarketAndLoadZone';
import { buildControlProviderFilter, getColumnFilteredValue, getColumnSortOrder } from '../../../components/table/columnFormatHelpers';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { useTableSelection } from '../../../components/table/useTableSelection';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { ViewSwitcher } from '../../../components/viewSwitcher/ViewSwitcher';
import { useAuth } from '../../../domain/auth/useAuth';
import { numberFormatter } from '../../../domain/common/numberFormatter';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../../domain/commonConst';
import { COMPANY_TYPE } from '../../../domain/company/interface';
import { DEFAULT_DATE_FORMAT, getUtcFormattedTime } from '../../../domain/date/date';
import { displayMetric } from '../../../domain/event-analytic/displayMetric';
import { IPaginationMeta } from '../../../domain/IPagination';
import { ENROLLMENT_STATUS_FILTER_VALUES, ENROLLMENT_STATUS_TEXT_MAP } from '../../../domain/meter-enrollment/interface';
import { buildProgramIsoTableFilter } from '../../../domain/nomination/buildProgramIsoTableFilter';
import { buildProgramUtilityTableFilter } from '../../../domain/nomination/buildProgramUtilityTableFilter';
import { INomination, NOMINATION_RECENCY_FILTER_VALUES, NOMINATION_RECENCY_TEXT_MAP } from '../../../domain/nomination/interface';
import { useNominationsListQuery } from '../../../domain/nomination/queries';
import { buildProgramFilter } from '../../../domain/program/buildProgramFilter';
import { useProgramListQuery } from '../../../domain/program/queries';
import { useMarketOptions } from '../../../domain/site/useMarketOptions';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { NominationSidebar } from '../nominationSidebar/NominationSidebar';
import { useNominationsSidebar } from '../nominationSidebar/useNominationsSidebar';
import { siteViewInterface } from './siteViewInterface';
import { NominationExportButton } from '../../../components/site/nominations/NominationExportButton';
import { WithControlledTooltip } from '../../../components/withControlledTooltip';
import './nominations.css';
import { isBefore, parseISO } from 'date-fns';

interface IPageState {
    companyId?: number;
    filter?: {};
    search?: string;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export const defaultState = {
    pagination: DEFAULT_PAGINATION,
};

type NOMINATION_PAGE_ACTION = PAGE_ACTION | 'READINESS_CONFIRMATION';

export const Nominations = () => {
    const auth = useAuth()!;
    const { notification } = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const { companies } = useCompanies()!;
    const { setPageQuery, queryToState } = usePageLocation({ parseNumbers: false });
    const { companyId, ...queryState } = queryToState(location.search);
    const [pageAction, setPageAction] = useState<NOMINATION_PAGE_ACTION | ''>();
    const { selectedKeys, selectedItems, handleSelectedItems } = useTableSelection('salesforce_id');
    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useNominationsSidebar();
    const ability = useContext(AbilityContext);
    const pageState: IPageState = isEmpty(queryState) ? { companyId, ...defaultState } : { companyId, ...queryState };
    const params = toUsefulParams(pageState);

    const listQuery = {
        pagination: params?.pagination ?? DEFAULT_PAGINATION,
        ...(params?.filter && { filter: params?.filter }),
        ...(params?.sorter && { sorter: params?.sorter }),
        ...(params?.search && { search: params?.search }),
        company_id: params?.company_id ?? null,
        include: 'latest_nomination_readiness_track',
    };
    const {
        data,
        isError,
        error,
        isFetching,
        refetch: refetchNominationList,
    } = useNominationsListQuery(listQuery, {
        keepPreviousData: true,
    });

    const nominations = data?.data || [];
    const meta = data?.meta || ({} as IPaginationMeta);

    if (isError) {
        notification.error({ key: 'nominations-fetch-error', message: error?.message || 'Cannot fetch nominations!' });
    }

    const marketZoneFilterOptions = useMarketOptions({ includeOther: true }, { enabled: !isFiltersSidebarEnabled });

    const { data: programsData } = useProgramListQuery({ company_id: params?.company_id ?? null }, { enabled: !isFiltersSidebarEnabled });
    const programs = useMemo(() => programsData?.data || [], [programsData?.data]);

    const controlProviderCompanies = useMemo(() => filterCompaniesByType(companies, COMPANY_TYPE.CONTROL_PROVIDER), [companies]);

    const onViewChange = (view: string) => {
        const companyQuery = pageState.companyId ? `?companyId=${pageState.companyId}` : '';
        navigate(`${view}${companyQuery}`);
    };

    async function onTableChange(
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<INomination> | SorterResult<INomination>[]
    ) {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, sorter, filter: newFilter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onSearch = async (value: string) => {
        setPageQuery({ ...pageState, search: value });

        // if search value is the same as in the state, then we need to refetch data and set pagination to 1 page
        if (value === (pageState.search || '')) {
            listQuery.pagination.current = DEFAULT_PAGINATION.current;

            await refetchNominationList(listQuery);
        }
    };

    const onSelectRow = (selectedRows: Key[]) => {
        handleSelectedItems(selectedRows as number[], nominations);
    };

    async function onModalClose(isRefreshNeeded: boolean = false) {
        setPageAction('');

        if (isRefreshNeeded) {
            handleSelectedItems([], nominations);
            await refetchNominationList(listQuery);
        }
    }

    const nominationColumns: CustomizableColumnType<INomination>[] = [
        {
            key: 'company_name',
            title: 'Company',
            dataIndex: 'company_name',
            render: (text, record) => record.meter_enrollment?.meter?.site?.company?.company_name,
        },
        {
            key: 'account_manager',
            title: 'Account Manager',
            dataIndex: 'account_manager',
            hidden: true, // Hide by default
            sorter: (a, b) =>
                compareWithLocale(a.meter_enrollment?.meter?.account?.account_manager, b.meter_enrollment?.meter?.account?.account_manager),
            ...getColumnSortOrder('account_manager', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.meter?.account?.account_manager,
        },
        {
            key: 'iso',
            title: 'ISO',
            dataIndex: 'iso',
            hidden: true, // Hide by default
            ...(isFiltersSidebarEnabled ? { filtered: false } : buildProgramIsoTableFilter(pageState)),
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.product?.program?.iso, b.meter_enrollment?.product?.program?.iso),
            ...getColumnSortOrder('iso', pageState.sorter),
            render: (text, record) => {
                return <UtilityOrIsoTag utilityOrIso={record.meter_enrollment?.product?.program?.iso} />;
            },
            width: 100,
        },
        {
            key: 'utility_id',
            title: 'Utility',
            dataIndex: 'utility_id',
            ...(isFiltersSidebarEnabled ? { filtered: false } : buildProgramUtilityTableFilter(pageState)),
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.product?.program?.utility_id, b.meter_enrollment?.product?.program?.utility_id),
            ...getColumnSortOrder('utility_id', pageState.sorter),
            render: (text, record) => {
                return <UtilityOrIsoTag utilityOrIso={record.meter_enrollment?.product?.program?.utility_id} />;
            },
            width: 100,
        },
        {
            key: 'lmp_market',
            title: 'Market, Load Zone',
            dataIndex: 'lmp_market',
            hidden: true, // Hide by default
            ...(isFiltersSidebarEnabled
                ? { filtered: false }
                : {
                      filterMultiple: true,
                      filters: marketZoneFilterOptions,
                      ...getColumnFilteredValue('lmp_market', pageState.filter),
                  }),
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.meter?.site?.lmp_market, b.meter_enrollment?.meter?.site?.lmp_market),
            ...getColumnSortOrder('lmp_market', pageState.sorter),
            render: (text, record) =>
                record.meter_enrollment?.meter?.site ? <SiteMarketAndLoadZone site={record.meter_enrollment?.meter?.site} /> : '',
        },
        {
            key: 'program_name',
            title: 'Program Name',
            dataIndex: 'program_name',
            ...(isFiltersSidebarEnabled
                ? { filtered: false }
                : {
                      filterMultiple: true,
                      filters: buildProgramFilter(programs),
                      ...getColumnFilteredValue('program_name', pageState?.filter || {}),
                      filterSearch: (input: string, record: any) => record?.fullText?.toLowerCase().includes(input.toLowerCase()),
                  }),
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.product?.program?.name, b.meter_enrollment?.product?.program?.name),
            ...getColumnSortOrder('program_name', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.product?.program?.name,
            width: 160,
        },
        {
            key: 'control_provider_name',
            title: 'Control Provider',
            dataIndex: 'control_provider_name',
            ...(isFiltersSidebarEnabled
                ? { filtered: false }
                : {
                      filterMultiple: true,
                      filters: buildControlProviderFilter(controlProviderCompanies, {
                          includeWithoutControlProvider: true,
                      }),
                      ...getColumnFilteredValue('control_provider_name', pageState.filter),
                  }),
            sorter: (a, b) =>
                compareWithLocale(
                    a.meter_enrollment?.meter?.site?.control_provider?.company_name,
                    b.meter_enrollment?.meter?.site?.control_provider?.company_name
                ),
            ...getColumnSortOrder('control_provider_name', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.meter?.site?.control_provider?.company_name,
        },
        {
            key: 'site_name',
            title: 'Site Name',
            dataIndex: 'site_name',
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.meter?.site?.site_name, b.meter_enrollment?.meter?.site?.site_name),
            ...getColumnSortOrder('site_name', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.meter?.site?.site_name,
        },
        {
            key: 'site_store_number',
            title: 'Store #',
            dataIndex: 'site_store_number',
            sorter: (a, b) =>
                compareWithLocale(a.meter_enrollment?.meter?.site?.site_store_number, b.meter_enrollment?.meter?.site?.site_store_number),
            ...getColumnSortOrder('site_store_number', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.meter?.site?.site_store_number,
        },
        {
            key: 'site_address',
            title: 'Address',
            dataIndex: 'site_address',
            sorter: (a, b) =>
                compareWithLocale(
                    `${a.meter_enrollment?.meter?.site?.site_state} ${a.meter_enrollment?.meter?.site?.site_city} ${a.meter_enrollment?.meter?.site?.site_address}`,
                    `${b.meter_enrollment?.meter?.site?.site_state} ${b.meter_enrollment?.meter?.site?.site_city} ${b.meter_enrollment?.meter?.site?.site_address}`
                ),
            ...getColumnSortOrder('site_address', pageState.sorter),
            render: (text, record) => (
                <SiteAddress
                    address={record.meter_enrollment?.meter?.site?.site_address}
                    city={record.meter_enrollment?.meter?.site?.site_city}
                    state={record.meter_enrollment?.meter?.site?.site_state}
                />
            ),
        },
        {
            key: 'enrollment_id',
            title: 'Enrollment ID',
            dataIndex: 'enrollment_id',
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.enrollment_id, b.meter_enrollment?.enrollment_id),
            ...getColumnSortOrder('enrollment_id', pageState.sorter),
            render: (text, record) => record.meter_enrollment?.enrollment_id,
        },
        {
            key: 'enrollment_status',
            title: 'Meter Enrollment Status',
            dataIndex: 'enrollment_status',
            ...(isFiltersSidebarEnabled
                ? { filtered: false }
                : {
                      filterMultiple: true,
                      filters: ENROLLMENT_STATUS_FILTER_VALUES,
                      ...getColumnFilteredValue('enrollment_status', pageState.filter),
                  }),
            sorter: (a, b) => compareWithLocale(a.meter_enrollment?.enrollment_status, b.meter_enrollment?.enrollment_status),
            ...getColumnSortOrder('enrollment_status', pageState.sorter),
            render: (text, record) => ENROLLMENT_STATUS_TEXT_MAP[record.meter_enrollment?.enrollment_status!],
        },
        {
            key: 'nomination_recency',
            title: 'Nomination Recency',
            dataIndex: 'nomination_recency',
            ...(isFiltersSidebarEnabled
                ? { filtered: false }
                : {
                      filterMultiple: true,
                      filters: NOMINATION_RECENCY_FILTER_VALUES,
                      ...getColumnFilteredValue('nomination_recency', pageState.filter),
                  }),
            sorter: (a, b) => compareWithLocale(a.recency, b.recency),
            ...getColumnSortOrder('nomination_recency', pageState.sorter),
            render: (text, record) => NOMINATION_RECENCY_TEXT_MAP[record.recency!],
        },
        {
            key: 'start_date',
            title: 'Start Date',
            dataIndex: 'start_date',
            sorter: (a, b) => compareWithLocale(a.start_date, b.start_date),
            ...getColumnSortOrder('start_date', pageState.sorter),
            render: (text, record) => getUtcFormattedTime(record.start_date, DEFAULT_DATE_FORMAT),
        },
        {
            key: 'end_date',
            title: 'End Date',
            dataIndex: 'end_date',
            sorter: (a, b) => compareWithLocale(a.end_date, b.end_date),
            ...getColumnSortOrder('end_date', pageState.sorter),
            render: (text, record) => getUtcFormattedTime(record.end_date, DEFAULT_DATE_FORMAT),
        },
        {
            key: 'nomination_kw',
            title: 'Nominated kW',
            dataIndex: 'nomination_kw',
            sorter: (a, b) => compareWithLocale(a.nomination_kw, b.nomination_kw),
            ...getColumnSortOrder('nomination_kw', pageState.sorter),
            render: (text, record) => numberFormatter.format(record.nomination_kw),
            width: 120,
        },
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      key: 'performance_kw',
                      title: 'Performance kW',
                      dataIndex: 'performance_kw',
                      sorter: (a: INomination, b: INomination) => compareWithLocale(a.performance_kw, b.performance_kw),
                      ...getColumnSortOrder('performance_kw', pageState.sorter),
                      render: (text: string, record: INomination) => displayMetric(record.performance_kw),
                      width: 120,
                  },
                  {
                      key: 'performance_percentage',
                      title: 'Performance %',
                      dataIndex: 'performance_percentage',
                      sorter: (a: INomination, b: INomination) => compareWithLocale(a.performance_percentage, b.performance_percentage),
                      ...getColumnSortOrder('performance_percentage', pageState.sorter),
                      render: (text: string, record: INomination) => displayMetric(record.performance_percentage, true),
                      width: 120,
                  },
              ]
            : []),
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      key: 'nomination_status',
                      title: 'Nomination Status',
                      render: (text: string, record: INomination) => {
                          const DATE_LIMIT = parseISO('2026-01-01T00:00:00.000Z');

                          const startDate = record?.start_date && parseISO(record.start_date);

                          const isAfterOrEqual = startDate && !isBefore(startDate, DATE_LIMIT);

                          // if the nomination is after 2025-01-01 -> we should show Ready for Customer Review status
                          return (
                              <>
                                  {(record?.latestReadinessTrack || isAfterOrEqual) && (
                                      <NominationStatusTag nominationReadinessTrack={record.latestReadinessTrack} />
                                  )}

                                  {record?.isLatestReadinessTrackOutdated ? (
                                      <Tooltip title="Changes have been made to the Nomination after the customer's last response!">
                                          <WarningOutlined style={{ color: WARNING_COLOR }} />
                                      </Tooltip>
                                  ) : (
                                      ''
                                  )}
                              </>
                          );
                      },
                      width: 250,
                      dataIndex: 'nomination_status',
                  },
              ]
            : []),
    ];

    useDocumentTitle('Nominations');

    return (
        <>
            <CustomizableTableContextProvider configKey="nominations" columns={nominationColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    {isFiltersSidebarEnabled && (
                        <NominationSidebar
                            collapsed={sidebarCollapsed}
                            onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                            totalRecords={meta?.total}
                            onFilterChange={() => {}}
                            gridComponent={<SidebarGrid />}
                        />
                    )}

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle={`Nominations`}
                            extra={[
                                <ViewSwitcher
                                    viewOptions={siteViewInterface(ability)}
                                    key="site-view-switcher"
                                    view={location.pathname}
                                    handleViewMode={onViewChange}
                                />,
                            ]}
                            actions={[
                                selectedItems?.length > 0 && (
                                    <Button key="readiness-confirmation-action" size="large" onClick={() => setPageAction('READINESS_CONFIRMATION')}>
                                        Season Readiness
                                    </Button>
                                ),
                                selectedItems.length > 0 && (
                                    <Button size="large" key="clear-selection-action" onClick={() => handleSelectedItems([], nominations)}>
                                        Clear Selected ({selectedItems.length})
                                    </Button>
                                ),
                                <SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="nomination-search" />,
                                <WithControlledTooltip
                                    key="nomination-csv-download"
                                    title="Please select company or any filter first!"
                                    extraVisibleCondition={isEmpty(params.filter) && !params.company_id}
                                >
                                    <NominationExportButton
                                        disabled={isEmpty(params.filter) && !params.company_id}
                                        filter={toUsefulParams({ ...pageState, companyId: companyId })}
                                    />
                                </WithControlledTooltip>,
                            ]}
                        />
                        <WithEmptyDataTable condition={isFetching}>
                            <CustomizableTable<INomination>
                                showHiddenColumnsCount
                                size="small"
                                rowKey="salesforce_id"
                                className="nominations-table"
                                sticky={{ offsetHeader: 62 }}
                                dataSource={nominations}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={meta}
                                rowSelection={
                                    auth.user?.isAdminRoleType()
                                        ? {
                                              selectedRowKeys: selectedKeys,
                                              preserveSelectedRowKeys: true,
                                              onChange: onSelectRow,
                                          }
                                        : undefined
                                }
                            />
                        </WithEmptyDataTable>

                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>

            {selectedItems.length > 0 && pageAction === 'READINESS_CONFIRMATION' && (
                <ScheduleNominationReadinessModal nominations={selectedItems} onClose={onModalClose} />
            )}
        </>
    );
};
